import * as Sentry from "@sentry/react";
import {
  getManagerProfilesRetrieveQueryOptions,
  getUsersRetrieveQueryOptions,
  type ForManagerLocationDetail,
} from "@sidegigllc/api/client";
import type { QueryClient } from "@tanstack/react-query";
import { locationQueryOptions } from "api/queries/locations";
import { env } from "config/env";
import { selectAuthUser } from "domains/auth/providers/Root/helpers";
import { initialize, type LDClient, type LDContext, type LDOptions } from "launchdarkly-react-client-sdk";
import { getActiveLocationId } from "services/atoms";
import { type AnalyticsUserTraits, getAnalyticsUserTraits } from "utils/analytics";
import { safeLocalStorage } from "utils/safeStorage";
import { defaultFlags, type FeatureFlagKey, type FeatureFlagValue } from "./featureFlags";

let bootstrap: LDOptions["bootstrap"] = "localStorage";
// Allow QA to set feature flags
if (env.REACT_APP_E2E_LAUNCH_DARKLY_FLAG_SOURCE != null) {
  const flagsRaw = safeLocalStorage.getItem(env.REACT_APP_E2E_LAUNCH_DARKLY_FLAG_SOURCE);
  if (flagsRaw != null) bootstrap = JSON.parse(flagsRaw);
}

export const context: LDContext = { anonymous: true };
export const options: LDOptions = {
  fetchGoals: false,
  bootstrap,
  sendEvents: !env.IS_LOCAL,
  inspectors: [Sentry.buildLaunchDarklyFlagUsedHandler()],
  ...(env.REACT_APP_LAUNCH_DARKLY_DEV_SERVER_URL
    ? {
        baseUrl: env.REACT_APP_LAUNCH_DARKLY_DEV_SERVER_URL,
        streamUrl: env.REACT_APP_LAUNCH_DARKLY_DEV_SERVER_URL,
        eventsUrl: env.REACT_APP_LAUNCH_DARKLY_DEV_SERVER_URL,
      }
    : {}),
};

export const ldClientPromise = new Promise<LDClient>((resolve) => {
  const clientSideID = env.REACT_APP_LAUNCH_DARKLY_DEV_SERVER_URL ? "default" : env.REACT_APP_LAUNCH_DARKLY_ID;
  const client = initialize(clientSideID, context, options);
  client.waitUntilReady().then(() => resolve(client));
});

type IdentifyParams = {
  user: AnalyticsUserTraits;
  location?: ForManagerLocationDetail;
};

export const identify = async ({ user, location }: IdentifyParams) => {
  if (!env.REACT_APP_LAUNCH_DARKLY_ID) return;
  const ldClient = await ldClientPromise;
  return await ldClient.identify({
    kind: "multi",
    user: {
      key: `${user.id}`,
      name: `${user.firstName} ${user.lastName}`,
      ...user,
      environment: env.REACT_APP_ENV,
    },
    location: location
      ? {
          key: location?.id,
          name: location?.name,
          market_id: location?.market_id,
          organization_id: location?.organization_id,
        }
      : undefined,
  });
};

export const getFeatureFlag = async <FlagKey extends FeatureFlagKey>(
  flagKey: FlagKey,
  params: { queryClient: QueryClient } | { global: true },
): Promise<FeatureFlagValue[FlagKey]> => {
  try {
    const activeLocationId = getActiveLocationId();
    const queryClient = "global" in params ? undefined : params.queryClient;
    const [client, user, manager, location] = await Promise.all([
      ldClientPromise,
      queryClient?.query({ ...getUsersRetrieveQueryOptions("me"), staleTime: "static" }),
      queryClient?.query({ ...getManagerProfilesRetrieveQueryOptions("me"), staleTime: "static" }),
      activeLocationId
        ? queryClient?.query({ ...locationQueryOptions(activeLocationId), staleTime: "static" })
        : Promise.resolve(undefined),
    ]);
    const context = client.getContext();
    if (user && (!context || context.anonymous)) {
      await identify({ user: getAnalyticsUserTraits(selectAuthUser(user), manager, activeLocationId), location });
    }
    return client.variation(flagKey, defaultFlags[flagKey]);
  } catch {
    return defaultFlags[flagKey];
  }
};
